/* eslint-disable @nx/enforce-module-boundaries */
import { InternalRefetchQueriesInclude } from '@apollo/client';
import {
  Button,
  Flex,
  Heading,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  Portal,
  Text,
  useToast,
} from '@chakra-ui/react';
import CloseSharpIcon from '@gamma/ui/icons/CloseSharpIcon';
import { Alert } from '@gamma/ui/overlay';
import { useSearchParams } from 'react-router';
import { i18n } from '@investigator/shared/localization';
import { useEffect, useMemo } from 'react';
import { IGetDetectionVariables } from '../../..';
import { DetectionQueryVariables } from '../../../../pages';
import {
  GET_DETECTIONS,
  IDetectionUpdateInfoInput,
  QUERY_DETECTIONS_PAGINATED,
  useSetStatusOnDetections,
} from '../../../../queries';
import { useDetectionContext } from '@investigator/features/detections/context';

const { excludeEntity } = i18n.pages.entityDetails;

const {
  setStatusOnDetectionsSuccessMessage,
  setStatusOnDetectionsErrorMessage,
  success,
  error,
} = excludeEntity;

const { cannotBeReverted, close, closeDetection } = i18n.pages.detections;

interface CloseDetectionContentProps {
  onClose: () => void;
  refetchQueries?: InternalRefetchQueriesInclude;
  refetchMain?: boolean;
  excludedEntitiesLoading?: boolean;
  setIsClosed?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  setPopoverType?: React.Dispatch<React.SetStateAction<string | null>>;
  getDetectionVariables?: IGetDetectionVariables;
  queryDetectionsPaginatedVariables?: DetectionQueryVariables;
  isLoading?: boolean;
}

export const CloseDetectionContent = ({
  onClose,
  setIsClosed,
  setIsLoading,
  setPopoverType,
  refetchQueries,
  excludedEntitiesLoading,
  getDetectionVariables,
  queryDetectionsPaginatedVariables,
  isLoading,
  refetchMain,
}: CloseDetectionContentProps) => {
  const toast = useToast();
  const { detectionData } = useDetectionContext();
  const [_, setSearchParams] = useSearchParams();

  const { detection_id, tenant_info, tenant } = detectionData || {};

  // Construct variables from detectionData if getDetectionVariables is undefined
  const refetchVariables =
    getDetectionVariables ||
    (detection_id && tenant
      ? {
          detection_ids: [
            {
              detection_id,
              tenant,
            },
          ],
        }
      : undefined);

  const refetchQueriesLocal = refetchVariables
    ? [
        {
          query: GET_DETECTIONS,
          variables: refetchVariables,
        },
      ]
    : [];

  const variables = {
    items: [
      {
        detection_id,
        // This may need to be updated in the future to allow for re-activating the detection
        detection_status: 'closed',
        update_info: {
          last_updated_timestamp: detectionData?.update_info
            ?.last_updated_timestamp as IDetectionUpdateInfoInput,
        },
        tenant: tenant_info?.tenant_id,
      },
    ],
  };

  const [setStatusOnDetections, { loading }] = useSetStatusOnDetections({
    variables,
    refetchQueries: refetchQueries
      ? refetchQueries
      : !queryDetectionsPaginatedVariables
        ? refetchQueriesLocal
        : [
            ...refetchQueriesLocal,
            {
              query: QUERY_DETECTIONS_PAGINATED,
              variables: queryDetectionsPaginatedVariables,
            },
          ],
    onCompleted: () => {
      setIsLoading && setIsLoading(false);
      toast({
        title: success,
        description: i18n.formatString(
          setStatusOnDetectionsSuccessMessage,
          detectionData?.alert_info?.alert_name as string,
        ),
        status: 'success',
        position: 'bottom-right',
        isClosable: true,
      });
      setPopoverType && setPopoverType(null);
      onClose();
      if (refetchMain) {
        setSearchParams((params) => {
          params.set('refetch_main', 'true');
          return params;
        });
      }
    },
    onError: () => {
      setIsLoading && setIsLoading(false);
      toast({
        title: error,
        description: i18n.formatString(
          setStatusOnDetectionsErrorMessage,
          detectionData?.alert_info?.alert_name as string,
        ),
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    },
  });

  const handleCloseDetection = () => {
    setIsLoading && setIsLoading(true);
    setStatusOnDetections();
  };

  const isStatusOpen = useMemo(() => {
    return detectionData?.detection_status === 'open';
  }, [detectionData]);

  useEffect(() => {
    setIsClosed && setIsClosed(!isStatusOpen);
  }, [isStatusOpen, setIsClosed]);

  return (
    <Portal>
      <PopoverContent w="320px" data-testid="close-detection-popover-content">
        <PopoverCloseButton top={1}>
          <CloseSharpIcon size="sm" />
        </PopoverCloseButton>
        <PopoverHeader>
          <Heading textStyle="body-md-bold">{close}</Heading>
        </PopoverHeader>
        <PopoverBody>
          <Text mb={2} textStyle="body-md">
            {closeDetection}
          </Text>
          <Alert variant="subtle" status="warning">
            {cannotBeReverted}
          </Alert>
        </PopoverBody>
        <PopoverFooter alignItems="end">
          <Flex justifyContent="end">
            <Button
              variant="solid"
              colorScheme="blue"
              data-testid="detection-close-button"
              onClick={() => {
                handleCloseDetection();
              }}
              isDisabled={!isStatusOpen || loading}
              isLoading={excludedEntitiesLoading || isLoading}
            >
              {close}
            </Button>
          </Flex>
        </PopoverFooter>
      </PopoverContent>
    </Portal>
  );
};
